import {
  Component,
  computed,
  inject,
  input,
  InputSignal,
  Signal,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import {
  CONFIGURATION_SELECT_OPTIONS_BY_SETTING_KEY,
  ConfigurationSettingKey,
  ESettingInputType,
  IConfiguration,
} from '@web/dtos';
import {
  ButtonGroupComponent,
  IButtonOption,
} from '../../../../components/data-input/form-inputs/button-group/button-group.component';
import { DropDownComponent } from '../../../../components/data-input/form-inputs/drop-down/drop-down.component';
import { IconPickerComponent } from '../../../../components/data-input/form-inputs/icon-picker/icon-picker.component';
import { MultiSelectDropDownComponent } from '../../../../components/data-input/form-inputs/multi-select-drop-down/multi-select-drop-down.component';
import { NumberInputComponent } from '../../../../components/data-input/form-inputs/number-input/number-input.component';
import { TextInputComponent } from '../../../../components/data-input/form-inputs/text-input/text-input.component';
import { ToggleComponent } from '../../../../components/data-input/form-inputs/toggle/toggle.component';
import { ToggleChipComponent } from '../../../../components/data-display/toggle-chip/toggle-chip.component';
import { TooltipComponent } from '../../../../components/feedback/tooltip/tooltip.component';
import { ConfigurationControlValue } from '../../../../entities/configuration-entity';
import { ConfigurationFormsFactoryService } from '../../../../factory/configuration/configuration-forms-factory.service';
import { CmsTextProviderService } from '../../../../services/cms-text-provider.service';
import { ConfigurationCustomInputComponent } from '../configuration-custom-input/configuration-custom-input.component';

@Component({
  selector: 'aw-configuration-setting-input',
  imports: [
    ButtonGroupComponent,
    ConfigurationCustomInputComponent,
    DropDownComponent,
    IconPickerComponent,
    MultiSelectDropDownComponent,
    NumberInputComponent,
    ReactiveFormsModule,
    TextInputComponent,
    ToggleChipComponent,
    ToggleComponent,
    TooltipComponent,
  ],
  templateUrl: './configuration-setting-input.component.html',
})
export class ConfigurationSettingInputComponent {
  private readonly configurationFactory: ConfigurationFormsFactoryService =
    inject(ConfigurationFormsFactoryService);

  protected readonly cmsTextProvider: CmsTextProviderService = inject(
    CmsTextProviderService
  );
  protected readonly inputType: typeof ESettingInputType = ESettingInputType;
  protected readonly tooltipHoverDelayMs: number = 500;

  readonly setting: InputSignal<IConfiguration> =
    input.required<IConfiguration>();
  readonly control: InputSignal<FormControl<ConfigurationControlValue>> =
    input.required<FormControl<ConfigurationControlValue>>();

  protected readonly inputId: Signal<string> = computed(
    (): string =>
      `configuration-${this.configurationFactory.controlKey(this.setting())}`
  );
  protected readonly selectOptions: Signal<string[]> = computed(
    (): string[] =>
      CONFIGURATION_SELECT_OPTIONS_BY_SETTING_KEY[
        this.setting().settingKey as ConfigurationSettingKey
      ] ?? []
  );
  protected readonly buttonOptions: Signal<IButtonOption<string>[]> = computed(
    (): IButtonOption<string>[] =>
      this.selectOptions().map((value: string) => ({
        label: value,
        value,
      }))
  );

  protected isMultiSelectOptionSelected(option: string): boolean {
    const value: ConfigurationControlValue = this.control().value;
    return Array.isArray(value) && value.includes(option);
  }

  protected toggleMultiSelectOption(option: string): void {
    const control: FormControl<ConfigurationControlValue> = this.control();
    if (control.disabled) {
      return;
    }

    const currentValue: Array<number | string> = Array.isArray(control.value)
      ? control.value
      : [];
    const updatedValue: Array<number | string> = currentValue.includes(option)
      ? currentValue.filter(
          (value: number | string): boolean => value !== option
        )
      : [...currentValue, option];

    control.setValue(updatedValue);
    control.markAsTouched();
    control.markAsDirty();
    control.updateValueAndValidity();
  }
}
